import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatHours } from "@/lib/employeeTimes";
import {
  createWorkTimeModel,
  deleteWorkTimeModel,
  listWorkTimeModels,
  updateWorkTimeModel,
  type WorkTimeModel,
  type WorkTimeModelDay,
} from "@/lib/workTimeModels";
import { AssignmentsPanel } from "@/pages/admin/zeiterfassung/einstellungen/AssignmentsPanel";
import { CalendarSettingsPanel } from "@/pages/admin/zeiterfassung/einstellungen/CalendarSettingsPanel";
import { ModelEditor } from "@/pages/admin/zeiterfassung/einstellungen/ModelEditor";
import { OooAutomationPanel } from "@/pages/admin/zeiterfassung/einstellungen/OooAutomationPanel";

type SettingsTab = "models" | "assignments" | "calendar" | "ooo";

export function ZeiterfassungSettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>("models");
  const [models, setModels] = useState<WorkTimeModel[]>([]);
  const [selectedId, setSelectedId] = useState<number | "new" | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listWorkTimeModels();
      setModels(rows);
      setSelectedId((current) => {
        if (current === "new") return current;
        if (current !== null && rows.some((m) => m.id === current)) return current;
        return rows[0]?.id ?? null;
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Modelle konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const selectedModel =
    selectedId === "new" ? null : models.find((m) => m.id === selectedId) ?? null;

  const handleSave = async (payload: { name: string; days: WorkTimeModelDay[] }) => {
    setSaving(true);
    try {
      if (selectedId === "new") {
        const created = await createWorkTimeModel(payload);
        setSelectedId(created.id);
      } else if (selectedModel) {
        await updateWorkTimeModel(selectedModel.id, payload);
      }
      setRefreshKey((k) => k + 1);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedModel || selectedModel.is_default) return;
    if (!window.confirm(`Modell „${selectedModel.name}" wirklich löschen?`)) return;
    setSaving(true);
    try {
      await deleteWorkTimeModel(selectedModel.id);
      setSelectedId(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell
      title="Zeiterfassung – Einstellungen"
      subtitle="Arbeitszeitmodelle definieren und Mitarbeitern zuweisen."
      back={{ to: "/zeiterfassung", label: "Zeiterfassung" }}
      actions={
        <Link
          to="/zeiterfassung"
          className="rounded-full border border-separator px-3 py-1.5 text-sm text-label-secondary transition hover:border-accent hover:text-accent"
        >
          Zurück
        </Link>
      }
    >
      <div className="mb-6">
        <SegmentedControl
          value={activeTab}
          onChange={setActiveTab}
          name="Einstellungen"
          size="md"
          options={[
            { value: "models", label: "Arbeitszeitmodelle" },
            { value: "assignments", label: "Mitarbeiter-Zuweisungen" },
            { value: "calendar", label: "Feiertage & Betriebsferien" },
            { value: "ooo", label: "Abwesenheitsnotiz" },
          ]}
        />
      </div>

      {error && (
        <div className="mb-4">
          <Alert variant="error" title="Fehler">{error}</Alert>
        </div>
      )}

      {activeTab === "models" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
          <div className="space-y-2 rounded-ios border border-separator bg-card p-3 shadow-ios">
            <div className="flex items-center justify-between px-1">
              <h3 className="text-sm font-semibold text-label">Modelle</h3>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedId("new")}
              >
                + Neu
              </Button>
            </div>
            {loading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : (
              <ul className="space-y-1">
                {models.map((model) => (
                  <li key={model.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(model.id)}
                      className={clsx(
                        "w-full rounded-lg px-3 py-2 text-left transition",
                        selectedId === model.id
                          ? "bg-accent-soft text-label"
                          : "hover:bg-fill-tertiary text-label-secondary",
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-label">{model.name}</span>
                        {model.is_default && (
                          <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[10px] font-semibold uppercase text-label-secondary">
                            Standard
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-label-secondary">
                        {formatHours(model.weekly_hours)} / Woche
                        {model.assigned_count > 0 && ` · ${model.assigned_count} zugewiesen`}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <ModelEditor
            key={selectedId === "new" ? "new" : String(selectedId ?? "none")}
            model={selectedModel}
            isNew={selectedId === "new"}
            saving={saving}
            onSave={handleSave}
            onDelete={selectedModel && !selectedModel.is_default ? handleDelete : undefined}
            onCancel={selectedId === "new" ? () => setSelectedId(models[0]?.id ?? null) : undefined}
          />
        </div>
      )}

      {activeTab === "assignments" && (
        <AssignmentsPanel
          models={models}
          refreshKey={refreshKey}
          onChanged={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {activeTab === "calendar" && (
        <CalendarSettingsPanel onChanged={() => setRefreshKey((k) => k + 1)} />
      )}

      {activeTab === "ooo" && (
        <OooAutomationPanel onChanged={() => setRefreshKey((k) => k + 1)} />
      )}
    </AppShell>
  );
}
